import React, { useEffect, useState } from "react";
import {
  Button, Table, Tag, Popconfirm, message, Modal,
  Form, Input, InputNumber, Select, Switch, DatePicker, Space, Tooltip,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  AptPromoCode, PromoCodeType,
  getAptPromoCodes, createAptPromoCode, updateAptPromoCode, deleteAptPromoCode,
} from "../../../utils/api/apartmentRentalsAPI";

interface Props { clientId: string }

const TYPE_LABELS: Record<PromoCodeType, string> = {
  percent: "Procenat",
  fixed: "Fiksni iznos",
};

const PromoCodesTab: React.FC<Props> = ({ clientId }) => {
  const [codes, setCodes] = useState<AptPromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<AptPromoCode | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const typeWatch = Form.useWatch("type", form);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAptPromoCodes(clientId);
      setCodes(res.data);
    } catch {
      message.error("Greška pri učitavanju promo kodova");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clientId]);

  const openCreate = () => {
    setEditData(null);
    form.resetFields();
    form.setFieldsValue({ type: "percent", isActive: true });
    setModalOpen(true);
  };

  const openEdit = (row: AptPromoCode) => {
    setEditData(row);
    form.setFieldsValue({
      code: row.code,
      type: row.type,
      value: row.value,
      description: row.description || "",
      isActive: row.isActive,
      validFrom: row.validFrom ? dayjs(row.validFrom) : null,
      validTo: row.validTo ? dayjs(row.validTo) : null,
      maxUses: row.maxUses ?? null,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        code: values.code?.toUpperCase().trim(),
        validFrom: values.validFrom ? values.validFrom.toISOString() : null,
        validTo: values.validTo ? values.validTo.toISOString() : null,
        maxUses: values.maxUses || null,
      };
      if (editData) {
        const res = await updateAptPromoCode(editData._id, payload);
        setCodes((prev) => prev.map((c) => c._id === editData._id ? res.data : c));
      } else {
        const res = await createAptPromoCode(clientId, payload);
        setCodes((prev) => [res.data, ...prev]);
      }
      setModalOpen(false);
      message.success(editData ? "Promo kod ažuriran" : "Promo kod kreiran");
    } catch (err: any) {
      if (err?.response?.data?.message) message.error(err.response.data.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAptPromoCode(id);
      setCodes((prev) => prev.filter((c) => c._id !== id));
      message.success("Promo kod obrisan");
    } catch {
      message.error("Greška pri brisanju");
    }
  };

  const handleToggleActive = async (row: AptPromoCode) => {
    try {
      const res = await updateAptPromoCode(row._id, { isActive: !row.isActive });
      setCodes((prev) => prev.map((c) => c._id === row._id ? res.data : c));
    } catch {
      message.error("Greška");
    }
  };

  const columns: ColumnsType<AptPromoCode> = [
    {
      title: "Kod",
      dataIndex: "code",
      render: (code: string) => (
        <Space>
          <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 13, letterSpacing: "0.05em" }}>{code}</span>
          <Tooltip title="Kopiraj">
            <Button
              type="text" size="small" icon={<CopyOutlined />}
              onClick={() => { navigator.clipboard.writeText(code); message.success("Kopirano"); }}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Tip / Vrijednost",
      render: (_, row) => (
        <span>
          {row.type === "percent"
            ? <Tag color="blue">{row.value}% popusta</Tag>
            : <Tag color="purple">-{row.value} fiksno</Tag>}
        </span>
      ),
    },
    {
      title: "Opis",
      dataIndex: "description",
      render: (v?: string) => v || <span style={{ color: "#9893a8" }}>—</span>,
    },
    {
      title: "Iskorištenost",
      render: (_, row) => (
        <Tooltip title={row.usedCount > 0 ? `${row.usedCount} rezervacija sa ovim kodom` : "Još nije korišten"}>
          <span style={{ fontSize: 13, cursor: "default" }}>
            <span style={{ fontWeight: row.usedCount > 0 ? 700 : 400, color: row.usedCount > 0 ? "#15803d" : "#9893a8" }}>
              {row.usedCount}
            </span>
            {row.maxUses ? <span style={{ color: "#9893a8" }}> / {row.maxUses}</span> : ""}
            <span style={{ fontSize: 11, color: "#9893a8", marginLeft: 4 }}>
              {row.usedCount > 0 ? "× iskorišten" : "× nije korišten"}
            </span>
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Važi",
      render: (_, row) => {
        if (!row.validFrom && !row.validTo) return <span style={{ color: "#9893a8", fontSize: 12 }}>Bez ograničenja</span>;
        const from = row.validFrom ? dayjs(row.validFrom).format("DD.MM.YY") : "—";
        const to = row.validTo ? dayjs(row.validTo).format("DD.MM.YY") : "—";
        return <span style={{ fontSize: 12 }}>{from} → {to}</span>;
      },
    },
    {
      title: "Aktivan",
      render: (_, row) => (
        <Switch
          checked={row.isActive}
          size="small"
          onChange={() => handleToggleActive(row)}
        />
      ),
    },
    {
      title: "",
      width: 80,
      render: (_, row) => (
        <Space>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm
            title="Obrisati promo kod?"
            onConfirm={() => handleDelete(row._id)}
            okText="Obriši" cancelText="Otkaži" okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "16px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1520" }}>Promo kodovi</div>
          <div style={{ fontSize: 12, color: "#9893a8", marginTop: 2 }}>
            Kodovi koji gosti unose pri rezervaciji za popust
          </div>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
          style={{ background: "#1a1520", borderColor: "#1a1520" }}>
          Novi promo kod
        </Button>
      </div>

      <Table
        rowKey="_id"
        dataSource={codes}
        columns={columns}
        loading={loading}
        pagination={false}
        size="small"
        locale={{ emptyText: "Nema promo kodova" }}
      />

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={editData ? "Izmijeni promo kod" : "Novi promo kod"}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setModalOpen(false)}>Otkaži</Button>
            <Button type="primary" loading={saving} onClick={handleSave}
              style={{ background: "#1a1520", borderColor: "#1a1520" }}>
              {editData ? "Sačuvaj" : "Kreiraj"}
            </Button>
          </div>
        }
        width={480}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="code" label="Promo kod"
            rules={[{ required: true, message: "Unesite kod" }, { pattern: /^[A-Za-z0-9_-]+$/, message: "Samo slova, brojevi, - i _" }]}
            tooltip="Automatski se pretvara u velika slova">
            <Input
              placeholder="npr. LJETO25"
              style={{ fontFamily: "monospace", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}
              disabled={!!editData}
              onChange={(e) => form.setFieldValue("code", e.target.value.toUpperCase())}
            />
          </Form.Item>

          <Form.Item name="description" label="Opis (opciono)">
            <Input placeholder="npr. Ljetni popust 2025" />
          </Form.Item>

          <Form.Item name="type" label="Tip popusta" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="percent">Procenat od ukupne cijene (%)</Select.Option>
              <Select.Option value="fixed">Fiksni iznos (oduzima se od ukupne)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="value"
            label={typeWatch === "percent" ? "Procenat popusta" : "Iznos popusta"}
            rules={[{ required: true, message: "Unesite vrijednost" }]}
          >
            <InputNumber
              min={1} max={typeWatch === "percent" ? 100 : undefined}
              style={{ width: "100%" }}
              addonAfter={typeWatch === "percent" ? "%" : "RSD / EUR"}
              placeholder={typeWatch === "percent" ? "npr. 15" : "npr. 1000"}
            />
          </Form.Item>

          <Form.Item name="maxUses" label="Maksimalan broj upotreba" tooltip="Prazno = neograničeno">
            <InputNumber min={1} style={{ width: "100%" }} placeholder="Neograničeno" />
          </Form.Item>

          <Form.Item label="Datum važenja (opciono)">
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item name="validFrom" noStyle>
                <DatePicker placeholder="Od datuma" format="DD.MM.YYYY" style={{ width: "50%" }} />
              </Form.Item>
              <Form.Item name="validTo" noStyle>
                <DatePicker placeholder="Do datuma" format="DD.MM.YYYY" style={{ width: "50%" }} />
              </Form.Item>
            </Space.Compact>
          </Form.Item>

          <Form.Item name="isActive" label="Aktivan" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PromoCodesTab;
